import { type Chat, isWhatsAppExport } from "@rhema/client";
import { Button, cn, EmptyState, focusRing, PageHeader, SearchField } from "@rhema/ui";
import { useEffect, useId, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { canPost, chatView } from "../data/view";
import { useImportQueue } from "../import/ImportProvider";
import { describeFile, formatBytes, kindOf } from "../media/prepare";
import { useClient, useClientState, useSession } from "../session/session";
import { ChatAvatar } from "./ChatList";

export const SHARE_CACHE = "rhema-share-v1";

/** Reads the files the service worker kept for this share, then deletes them from the cache. */
export async function takeSharedFiles(inbox: string): Promise<File[]> {
  if (!inbox || typeof caches === "undefined") return [];
  try {
    const cache = await caches.open(SHARE_CACHE);
    const prefix = `/share-inbox/${inbox}/`;
    const keys = (await cache.keys()).filter((r) => new URL(r.url).pathname.startsWith(prefix));
    keys.sort((a, b) => a.url.localeCompare(b.url, undefined, { numeric: true }));
    const files: File[] = [];
    for (const key of keys) {
      const res = await cache.match(key);
      if (!res) continue;
      const blob = await res.blob();
      const name = decodeURIComponent(res.headers.get("X-File-Name") ?? "file");
      files.push(new File([blob], name, { type: blob.type || res.headers.get("Content-Type") || "" }));
      await cache.delete(key);
    }
    return files;
  } catch {
    return [];
  }
}

/** The text of a share: its text, then its link when the text does not already hold it. */
export function sharedText(params: URLSearchParams): string {
  const title = params.get("title")?.trim() ?? "";
  const text = params.get("text")?.trim() ?? "";
  const url = params.get("url")?.trim() ?? "";
  const parts = [text || title];
  if (url && !text.includes(url)) parts.push(url);
  return parts.filter(Boolean).join("\n");
}

/**
 * Something shared to Rhema from another app, such as a photo from Penielly or a message from
 * Malakh. The person picks a chat, and it goes out encrypted like anything else they send.
 */
export function ShareScreen() {
  const client = useClient();
  const { platform } = useSession();
  const state = useClientState();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const imports = useImportQueue();
  const textId = useId();
  const [text, setText] = useState(() => sharedText(params));
  const [files, setFiles] = useState<File[] | null>(null);
  const [query, setQuery] = useState("");
  const [chatId, setChatId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const inbox = params.get("inbox") ?? "";
  // Android passes the files another app shared as paths in its cache.
  const nativeFiles = params.getAll("file").join("\n");

  useEffect(() => {
    const paths = nativeFiles ? nativeFiles.split("\n") : [];
    const read = paths.length && platform.sharedFiles ? platform.sharedFiles(paths) : takeSharedFiles(inbox);
    void read.then(setFiles, () => setFiles([]));
  }, [inbox, nativeFiles, platform]);

  const chats = useMemo(
    () =>
      state.chatOrder
        .map((id) => state.chats[id])
        .filter((c): c is Chat => Boolean(c && !c.archived && canPost(c)))
        .map((c) => ({ chat: c, view: chatView(c, state) }))
        .filter(({ view }) => view.title.toLowerCase().includes(query.trim().toLowerCase())),
    [state, query],
  );

  // A WhatsApp chat export shared from WhatsApp's own share sheet goes to the import queue instead.
  const [isExport, setIsExport] = useState(false);
  useEffect(() => {
    let live = true;
    void Promise.all((files ?? []).map((f) => isWhatsAppExport(f))).then((found) => {
      if (live) setIsExport(found.some(Boolean));
    });
    return () => {
      live = false;
    };
  }, [files]);

  const [reading, setReading] = useState(false);
  const importChat = async () => {
    if (!files) return;
    setReading(true);
    const added = await imports.add(files.map((file) => ({ file })));
    const ready = added.length === 1 && added[0]?.status === "setup" ? added[0].id : null;
    navigate(ready ? `/import?setup=${ready}` : "/import", { replace: true });
  };

  const nothing = files !== null && !files.length && !text.trim();
  const chosen = chatId ? state.chats[chatId] : undefined;

  const send = async () => {
    if (!chatId) return;
    setSending(true);
    setError(null);
    try {
      const body = text.trim();
      if (body) await client.send(chatId, { type: "text", body });
      for (const file of files ?? []) {
        const kind = kindOf(file);
        const meta = await describeFile(file, kind);
        void client.sendMedia(chatId, file, { kind, meta }).catch(() => undefined);
      }
      navigate(`/chats/${chatId}`, { replace: true });
    } catch (e) {
      setError((e as Error).message || "That could not be sent.");
      setSending(false);
    }
  };

  if (nothing) {
    return (
      <EmptyState title="Nothing to share" className="flex-1">
        The app that opened Rhema did not send anything. Go back and try sharing again.
      </EmptyState>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-linen">
      <div className="mx-auto w-full max-w-[640px] pb-10">
        <PageHeader title="Share to Rhema" size="md" />
        <div className="flex flex-col gap-3 px-4">
          {isExport && (
            <section className="flex flex-col gap-2 rounded-card border border-blue bg-blue-tint p-4">
              <h2 className="m-0 text-[15px] font-semibold">This is a WhatsApp chat export</h2>
              <p className="m-0 text-sm text-ink-secondary">
                Import it to bring the whole conversation into a Rhema chat, with each message under its
                original sender and date.
              </p>
              <Button className="self-start" disabled={reading} onClick={() => void importChat()}>
                {reading ? "Reading the export" : "Import this chat"}
              </Button>
            </section>
          )}
          {files === null && (
            <p role="status" className="m-0 text-sm text-ink-meta">
              Getting what you shared.
            </p>
          )}
          {files && files.length > 0 && (
            <ul aria-label="Shared files" className="m-0 flex list-none flex-col gap-1 p-0">
              {files.map((f, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: shared files keep their order
                <li key={i} className="flex justify-between gap-2 text-sm">
                  <span className="truncate font-semibold">{f.name}</span>
                  <span className="text-ink-secondary">{formatBytes(f.size)}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-col gap-1.5">
            <label htmlFor={textId} className="text-[13px] font-semibold text-ink">
              Message
            </label>
            <textarea
              id={textId}
              rows={3}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className={cn(
                "rounded-field border border-line bg-surface px-4 py-3 font-sans text-[15px] text-ink focus:border-blue",
                focusRing,
              )}
            />
          </div>

          <SearchField label="Search chats" placeholder="Search chats" value={query} onChange={setQuery} />
          <ul aria-label="Choose a chat" className="m-0 flex list-none flex-col gap-1 p-0">
            {chats.map(({ chat, view }) => (
              <li key={chat.id}>
                <button
                  type="button"
                  aria-pressed={chatId === chat.id}
                  onClick={() => setChatId(chat.id)}
                  className={cn(
                    "flex w-full cursor-pointer items-center gap-3 rounded-card border bg-surface px-3 py-2.5 text-left",
                    chatId === chat.id ? "border-blue" : "border-line",
                    focusRing,
                  )}
                >
                  <ChatAvatar chat={view} size={38} />
                  <span className="truncate text-[15px] font-semibold">{view.title}</span>
                </button>
              </li>
            ))}
            {!chats.length && <li className="text-sm text-ink-secondary">No chats match.</li>}
          </ul>

          {error && (
            <p role="alert" className="m-0 text-sm font-semibold text-scarlet">
              {error}
            </p>
          )}
          <Button size="lg" block disabled={!chosen || sending || files === null} onClick={() => void send()}>
            {sending ? "Sending" : chosen ? `Send to ${chatView(chosen, state).title}` : "Choose a chat"}
          </Button>
          <p className="m-0 text-center text-xs text-ink-meta">
            It is end-to-end encrypted when you send it.
          </p>
        </div>
      </div>
    </div>
  );
}
